/* eslint-disable react-hooks/exhaustive-deps */
import React, { ChangeEvent, FormEvent, useState, useEffect } from "react";
import { Modal, ModalOverlay, ModalContent, ModalBody } from "@chakra-ui/modal";
import Card from "components/card";
import RoleDropdown from "views/admin/user/components/dropdown/RoleDropdown";
import MerchantDropdown from "views/admin/user/components/dropdown/MerchantDropdown";
import SubaccountDropdown from "views/admin/user/components/dropdown/SubaccountDropdown";
import axios from "axios";
import { Button } from "@chakra-ui/react";
import SweetAlert2 from "react-sweetalert2";

interface AddRoleModalProps {
  setSwalProps: any;
  isOpen: boolean;
  onClose: () => void;
  userName: string;
  userEmail: string;
  roleOptions: string[];
  merchants: { id: string; name: string }[] | null;
  getSubaccountOptions: (
    merchantId: string
  ) => Promise<{ id: string; suba_name: string }[]>;
  isSetSubaccounts: boolean;
  fetchUserRoles: any;
}

const AddRoleModal: React.FC<AddRoleModalProps> = ({
  setSwalProps,
  isOpen,
  onClose,
  userName,
  userEmail,
  roleOptions,
  merchants,
  getSubaccountOptions,
  isSetSubaccounts,
  fetchUserRoles
}) => {
  const [newRole, setNewRole] = useState({
    role: "",
    merchant: "",
    subaccount: "",
  });
  const [filteredSubaccounts, setFilteredSubaccounts] = useState<
    { id: string; suba_name: string }[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const sysUsername = localStorage.getItem("username")?.toLowerCase();

  useEffect(() => {
    if (newRole.merchant) {
      const fetchSubaccounts = async () => {
        const fetchedSubaccounts = await getSubaccountOptions(newRole.merchant);
        setFilteredSubaccounts(fetchedSubaccounts);
      };
      fetchSubaccounts();
    } else {
      setFilteredSubaccounts([]);
    }
  }, [newRole.merchant]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === "role") {
      setNewRole((prevRole) => ({
        ...prevRole,
        [name]: value,
        merchant: "",
        subaccount: "",
      }));
      setFilteredSubaccounts([]);
    } else {
      setNewRole((prevRole) => ({
        ...prevRole,
        [name]: value,
      }));
    }
  };

  const resetForm = () => {
    setNewRole({
      role: "",
      merchant: "",
      subaccount: "",
    });
  };

  const isFormValid = newRole !== null ? (
    newRole.role
    && newRole.merchant
    && (newRole.role === "Merchant" || newRole.subaccount)
  ) : false;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const postData: any = {
        email: userEmail,
        username: userName,
        role: newRole.role,
        merchantId: newRole.merchant,
        userRole,
        sysUsername,
      };
      if (newRole.role !== "Merchant") {
        postData.subaccountId = newRole.subaccount;
      }

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/${userRole}/user/action/add-user-role`,
        postData
      );

      // Close modal first, then show success message
      setIsSubmitting(false);
      onClose();

      setSwalProps({
        show: true,
        icon: "success",
        title: "Success",
        html: "Role successfully added!",
        showConfirmButton: true,
        didClose: () => {
          resetForm();
          setSwalProps({});
          fetchUserRoles(true);
        },
      });
    } catch (error: any) {
      setIsSubmitting(false);
      resetForm();
      onClose();

      setSwalProps({
        show: true,
        icon: "error",
        title: "Oops!",
        html: error?.response?.data?.message || "Failed to add role, please try again",
        showConfirmButton: true,
        didClose: () => {
          setSwalProps({});
        },
      });
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        closeOnOverlayClick={false}
      >
        <ModalOverlay className="bg-[#000] !opacity-30" />
        <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
          <ModalBody className="p-6 md:p-7">
            <div className="mb-4 flex items-start gap-3">
              <div className="rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-400 dark:ring-primary-900/30">
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="currentColor"
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Add Role
                </h1>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  Assign a new role to this user
                </p>
              </div>
            </div>

            <div className="mb-5 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/5">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Username:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{userName}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Email:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{userEmail}</span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-5">
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                  Role <span className="text-red-500">*</span>
                </label>
                <RoleDropdown
                  isDisabled={isSubmitting}
                  newUser={newRole}
                  handleInputChange={handleInputChange}
                  roleOptions={roleOptions}
                />
              </div>

              <div className="mb-5">
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                  Merchant <span className="text-red-500">*</span>
                </label>
                <MerchantDropdown
                  isDisabled={isSubmitting}
                  newUser={{ merchant: newRole.merchant }}
                  handleInputChange={handleInputChange}
                  merchants={merchants}
                />
              </div>

              {newRole.role !== "Merchant" &&
                <div className="mb-5">
                  <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                    Subaccount <span className="text-red-500">*</span>
                  </label>
                  <SubaccountDropdown
                    isDisabled={isSubmitting}
                    newUser={{ subaccount: newRole.subaccount }}
                    handleInputChange={handleInputChange}
                    merchants={merchants}
                    subaccounts={filteredSubaccounts}
                    isSetSubaccounts={isSetSubaccounts}
                  />
                </div>
              }

              <div className="flex justify-end gap-2">
                <Button
                  onClick={() => {
                    resetForm();
                    onClose();
                  }}
                  isDisabled={isSubmitting}
                  className="dark:bg-neutral-900 dark:hover:bg-neutral-800 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-white/10 dark:text-white"
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  isDisabled={!isFormValid || isSubmitting}
                  isLoading={isSubmitting}
                  loadingText="Adding..."
                  className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  Add
                </Button>
              </div>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AddRoleModal;